import { useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import {
  CheckIcon,
  FilterIcon,
  ImageIcon,
  ReceiptTextIcon,
  ShieldAlertIcon,
  UtensilsIcon,
  XIcon,
} from 'lucide-react';
import {
  PhotoScanStatus,
  PhotoSubject,
  ReceiptStatus,
  type AdminFraudTicket,
  type AdminPhotoRow,
  type AdminReceiptRow,
} from '@qaytim/shared';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DatePicker } from '@/components/date-picker';
import { Field } from '@/components/field';
import { PageHeader } from '@/components/page-header';
import { PagerControls } from '@/components/pager';
import { Tabs } from '@/components/tabs';
import { useOffsetPager } from '@/hooks/use-offset-pager';
import { StatusBadge } from '@/components/status-badge';
import { EmptyState, ErrorNotice, SuccessNotice, TableSkeleton } from '@/components/states';
import {
  useAdminPhotoScans,
  useAdminReceipts,
  useAdminStats,
  useFraudQueue,
  useModerateTicket,
} from '@/lib/admin-queries';
import { formatNumber } from '@/lib/utils';
import { formatTashkent } from '@/lib/time';
import { strings } from '@/i18n/strings';

const RECEIPT_STATUSES = [
  ReceiptStatus.PENDING_DETAILS,
  ReceiptStatus.DETAILS_FETCHING,
  ReceiptStatus.DETAILS_READY,
  ReceiptStatus.DETAILS_FAILED,
];

const PHOTO_STATUSES = [
  PhotoScanStatus.AWAITING_UPLOAD,
  PhotoScanStatus.IN_REVIEW,
  PhotoScanStatus.CONFIRMED,
  PhotoScanStatus.REJECTED,
  PhotoScanStatus.VOID,
];

export const MODERATION_TABS = ['receipts', 'photos', 'meals', 'fraud'] as const;
export type ModerationTab = (typeof MODERATION_TABS)[number];

/**
 * Moderatsiya (master-promt §5 P4.2b).
 *
 * **Nega tab, nega bitta sahifada emas.** Ilgari uchta ro'yxat ustma-ust turardi va bu
 * ular qisqa bo'lgan paytda ishlagan. Sahifalash kelgach ekran uch metrga cho'zildi: pastdagi
 * jadvalning sahifalash tugmasiga yetish uchun yuqoridagi ikkitasini o'tib ketish kerak edi,
 * va "qaysi jadvalning sahifasidaman" degan savol yo'qolardi. Bundan tashqari sahifa
 * ochilishi bilan uchta so'rov ketardi, holbuki operator bir vaqtda bitta ro'yxat bilan
 * ishlaydi.
 *
 * **Nega rasm va ovqat alohida tab.** Ular bitta jadvalda (`photo_scans.subject`), ammo
 * boshqa oqim, boshqa kunlik chegara va boshqa to'lov. Aralash ro'yxatda "bugungi ovqat
 * skanlari qanday" degan savolga javob berib bo'lmasdi.
 *
 * Holat qiymatlari ekranda TARJIMA QILINMAYDI — ular `receipts.status` ustunidagi qiymatning
 * o'zi, va operator qo'llab-quvvatlash so'rovida aynan shu so'zni izlaydi.
 */
export function ModerationPage() {
  const { tab, page } = useSearch({ from: '/shell/moderation' });
  const navigate = useNavigate({ from: '/moderation' });
  const stats = useAdminStats();

  /** Tab almashganda sahifa BOSHIGA qaytadi — 4-sahifa boshqa ro'yxatda begona. */
  const selectTab = (next: ModerationTab) => {
    void navigate({ search: { tab: next, page: 1 } });
  };

  const setPage = (next: number) => {
    void navigate({ search: (current) => ({ ...current, page: next }) });
  };

  return (
    <section className="flex flex-col gap-6">
      <PageHeader title={strings.moderation.title} description={strings.moderation.subtitle} />

      <Tabs
        label={strings.moderation.tabsLabel}
        value={tab}
        onChange={selectTab}
        tabs={[
          {
            value: 'receipts',
            label: strings.moderation.tabReceipts,
            icon: ReceiptTextIcon,
            count: stats.data?.receipts.total,
          },
          {
            value: 'photos',
            label: strings.moderation.tabPhotos,
            icon: ImageIcon,
            count: stats.data?.photos.total,
          },
          {
            value: 'meals',
            label: strings.moderation.tabMeals,
            icon: UtensilsIcon,
            count: stats.data?.meals.total,
          },
          {
            value: 'fraud',
            label: strings.moderation.tabFraud,
            icon: ShieldAlertIcon,
            count: stats.data?.queues.fraudReview,
            // Yagona navbat — qolgan uchtasi arxiv. Nolga tushishi kerak bo'lgan raqam shu.
            urgent: true,
          },
        ]}
      >
        {tab === 'receipts' ? <ReceiptList page={page} onPageChange={setPage} /> : null}
        {tab === 'photos' ? (
          <PhotoList
            subject={PhotoSubject.PRODUCT}
            heading={strings.moderation.photosHeading}
            subtitle={strings.moderation.photosSubtitle}
            page={page}
            onPageChange={setPage}
          />
        ) : null}
        {tab === 'meals' ? (
          <PhotoList
            subject={PhotoSubject.FOOD}
            heading={strings.moderation.mealsHeading}
            subtitle={strings.moderation.mealsSubtitle}
            page={page}
            onPageChange={setPage}
          />
        ) : null}
        {tab === 'fraud' ? <FraudQueue page={page} onPageChange={setPage} /> : null}
      </Tabs>
    </section>
  );
}

/** Har bir tab paneli sahifa raqamini URL dan oladi — detail sahifadan qaytish uni saqlaydi. */
interface TabPanelProps {
  page: number;
  onPageChange: (page: number) => void;
}

const RECEIPT_COLUMNS = 9;

function ReceiptList({ page, onPageChange }: TabPanelProps) {
  const pager = useOffsetPager({ page, onPageChange });
  const [applied, setApplied] = useState<{
    status?: string;
    userId?: string;
    from?: string;
    to?: string;
  }>({});
  const [draft, setDraft] = useState({ status: '', userId: '', from: '', to: '' });

  const receipts = useAdminReceipts({ ...applied, page: pager.page, limit: pager.limit });

  const apply = (event: React.FormEvent) => {
    event.preventDefault();
    // Sahifa har filtr o'zgarishida tashlanadi: aks holda yangi filtr eski sahifadan boshlanardi.
    pager.reset();
    setApplied({
      status: draft.status || undefined,
      userId: draft.userId.trim() || undefined,
      from: draft.from || undefined,
      to: draft.to || undefined,
    });
  };

  const reset = () => {
    pager.reset();
    setDraft({ status: '', userId: '', from: '', to: '' });
    setApplied({});
  };

  const rows = receipts.data?.items ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{strings.moderation.receiptsHeading}</CardTitle>
        <CardDescription>{strings.moderation.receiptsSubtitle}</CardDescription>
        <CardAction>
          {/* Kartochka yorlig'i endi SAHIFADAGI emas, jamidagi sanoqni ko'rsatadi. */}
          <Badge variant="secondary" className="tabular">
            {receipts.data?.total ?? 0}
          </Badge>
        </CardAction>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        <form
          className="bg-muted/40 grid gap-3 rounded-lg border p-3 md:grid-cols-2 xl:grid-cols-5"
          onSubmit={apply}
        >
          <Field label={strings.moderation.filterStatus}>
            <Select
              name="receiptStatus"
              value={draft.status}
              onChange={(event) => setDraft({ ...draft, status: event.target.value })}
            >
              <option value="">{strings.moderation.filterAll}</option>
              {RECEIPT_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={strings.moderation.filterUser}>
            <Input
              name="receiptUserId"
              className="font-mono"
              value={draft.userId}
              onChange={(event) => setDraft({ ...draft, userId: event.target.value })}
            />
          </Field>

          <Field label={strings.moderation.filterFrom}>
            <DatePicker
              name="receiptFrom"
              ariaLabel={strings.moderation.filterFrom}
              value={draft.from}
              onChange={(value) => setDraft({ ...draft, from: value })}
            />
          </Field>

          <Field label={strings.moderation.filterTo}>
            <DatePicker
              name="receiptTo"
              ariaLabel={strings.moderation.filterTo}
              value={draft.to}
              onChange={(value) => setDraft({ ...draft, to: value })}
            />
          </Field>

          <div className="flex items-end gap-2">
            <Button type="submit" size="sm">
              <FilterIcon aria-hidden="true" />
              {strings.moderation.apply}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={reset}>
              {strings.common.reset}
            </Button>
          </div>
        </form>

        {receipts.isError ? <ErrorNotice error={receipts.error} /> : null}

        {receipts.isSuccess && rows.length === 0 ? (
          <EmptyState title={strings.app.empty} icon={ReceiptTextIcon} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{strings.moderation.columnUser}</TableHead>
                <TableHead>{strings.moderation.columnScannedAt}</TableHead>
                <TableHead>{strings.draws.columnStatus}</TableHead>
                <TableHead className="text-right">{strings.moderation.columnTotal}</TableHead>
                <TableHead className="text-right">{strings.moderation.columnPoints}</TableHead>
                <TableHead className="text-right">{strings.moderation.columnItems}</TableHead>
                <TableHead>{strings.moderation.columnVerifiedBy}</TableHead>
                <TableHead>{strings.moderation.columnError}</TableHead>
                <TableHead>{strings.moderation.columnActions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {receipts.isPending ? <TableSkeleton columns={RECEIPT_COLUMNS} /> : null}
              {rows.map((row: AdminReceiptRow) => (
                <TableRow key={row.id}>
                  <TableCell className="tabular font-mono">{row.telegramId}</TableCell>
                  <TableCell className="tabular whitespace-nowrap">
                    {formatTashkent(row.scannedAt)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <span className="flex items-center gap-1.5">
                      <StatusBadge>{row.status}</StatusBadge>
                      {row.revoked ? (
                        <Badge variant="destructive">{strings.moderation.revoked}</Badge>
                      ) : null}
                    </span>
                  </TableCell>
                  {/*
                    Summa va ball mingliklar bo'shlig'i bilan: bu ustunlarda olti xonali
                    qiymatlar odatiy (`1284000`), va ajratuvchisiz ular jadvalda o'qib
                    bo'lmaydigan raqamlar ustuniga aylanadi.
                  */}
                  <TableCell className="tabular text-right">
                    {row.totalAmount === null
                      ? strings.draws.noWinner
                      : formatNumber(row.totalAmount)}
                  </TableCell>
                  <TableCell className="tabular text-right">
                    {row.pointsAwarded === null
                      ? strings.draws.noWinner
                      : formatNumber(row.pointsAwarded)}
                  </TableCell>
                  <TableCell className="tabular text-right">
                    {formatNumber(row.itemCount)}
                  </TableCell>
                  <TableCell>{row.totalVerifiedBy ?? strings.draws.noWinner}</TableCell>
                  <TableCell className="text-destructive max-w-64 truncate text-xs">
                    {row.lastError ?? strings.draws.noWinner}
                  </TableCell>
                  <TableCell>
                    <Button size="sm" variant="outline" asChild>
                      <Link to="/moderation/receipts/$receiptId" params={{ receiptId: row.id }}>
                        {strings.common.open}
                      </Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <PagerControls
          pager={pager}
          total={receipts.data?.total}
          pageCount={receipts.data?.pageCount}
          rowCount={rows.length}
          busy={receipts.isFetching}
          label={strings.moderation.receiptsHeading}
        />
      </CardContent>
    </Card>
  );
}

const PHOTO_COLUMNS = 7;

interface PhotoListProps extends TabPanelProps {
  subject: PhotoSubject;
  heading: string;
  subtitle: string;
}

/**
 * Rasm skanlari ro'yxati — mahsulot yoki ovqat.
 *
 * Bitta komponent ikkala tabga xizmat qiladi, chunki ustunlar va filtrlar bir xil; farq
 * faqat `subject` da. Ikki nusxa yozish ustunlar vaqt o'tishi bilan ajralib ketishiga olib
 * kelardi, holbuki ular bir xil savolga javob beradi.
 */
function PhotoList({ subject, heading, subtitle, page, onPageChange }: PhotoListProps) {
  const pager = useOffsetPager({ page, onPageChange });
  const [status, setStatus] = useState('');
  const [userIdDraft, setUserIdDraft] = useState('');
  const [userId, setUserId] = useState('');

  const photos = useAdminPhotoScans({
    subject,
    status: status || undefined,
    userId: userId || undefined,
    page: pager.page,
    limit: pager.limit,
  });

  const rows = photos.data?.items ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{heading}</CardTitle>
        <CardDescription>{subtitle}</CardDescription>
        <CardAction>
          <Badge variant="secondary" className="tabular">
            {photos.data?.total ?? 0}
          </Badge>
        </CardAction>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        <form
          className="bg-muted/40 grid gap-3 rounded-lg border p-3 md:grid-cols-2 xl:grid-cols-4"
          onSubmit={(event) => {
            event.preventDefault();
            pager.reset();
            setUserId(userIdDraft.trim());
          }}
        >
          <Field label={strings.moderation.filterStatus}>
            <Select
              name="photoStatus"
              value={status}
              onChange={(event) => {
                pager.reset();
                setStatus(event.target.value);
              }}
            >
              <option value="">{strings.moderation.filterAll}</option>
              {PHOTO_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={strings.moderation.filterUser}>
            <Input
              name="photoUserId"
              className="font-mono"
              value={userIdDraft}
              onChange={(event) => setUserIdDraft(event.target.value)}
            />
          </Field>

          <div className="flex items-end">
            {/*
              Sahifada ikkita filtr formasi bor, ya'ni ikkita "Filtrlash" tugmasi. Ko'z ularni
              turgan kartochkasi bo'yicha ajratadi, ekran o'quvchisi esa — faqat nomi bo'yicha,
              shuning uchun bu tugmaning nomiga bo'lim qo'shiladi.
            */}
            <Button type="submit" size="sm" aria-label={`${strings.moderation.apply} — ${heading}`}>
              <FilterIcon aria-hidden="true" />
              {strings.moderation.apply}
            </Button>
          </div>
        </form>

        {photos.isError ? <ErrorNotice error={photos.error} /> : null}

        {photos.isSuccess && rows.length === 0 ? (
          <EmptyState title={strings.app.empty} icon={ImageIcon} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{strings.moderation.columnUser}</TableHead>
                <TableHead>{strings.moderation.columnScannedAt}</TableHead>
                <TableHead>{strings.draws.columnStatus}</TableHead>
                <TableHead className="text-right">{strings.moderation.columnRecognised}</TableHead>
                <TableHead className="text-right">{strings.moderation.columnItems}</TableHead>
                <TableHead className="text-right">{strings.moderation.columnPoints}</TableHead>
                <TableHead>{strings.moderation.columnActions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {photos.isPending ? <TableSkeleton columns={PHOTO_COLUMNS} /> : null}
              {rows.map((row: AdminPhotoRow) => (
                <TableRow key={row.id}>
                  <TableCell className="tabular font-mono">{row.telegramId}</TableCell>
                  <TableCell className="tabular whitespace-nowrap">
                    {formatTashkent(row.createdAt)}
                  </TableCell>
                  <TableCell>
                    <StatusBadge>{row.status}</StatusBadge>
                  </TableCell>
                  <TableCell className="tabular text-right">
                    {formatNumber(row.recognisedItemCount)}
                  </TableCell>
                  <TableCell className="tabular text-right">
                    {formatNumber(row.itemCount)}
                  </TableCell>
                  <TableCell className="tabular text-right">
                    {row.pointsAwarded === null
                      ? strings.draws.noWinner
                      : formatNumber(row.pointsAwarded)}
                  </TableCell>
                  <TableCell>
                    {/*
                      Rasmning O'ZI faqat detail sahifada: jadvalda thumbnail chizish har
                      sahifada N ta imzolangan havola yaratishni talab qilardi, va ularning
                      aksariyati hech qachon ochilmasdi.
                    */}
                    <Button size="sm" variant="outline" asChild>
                      <Link to="/moderation/photos/$photoScanId" params={{ photoScanId: row.id }}>
                        {strings.common.open}
                      </Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <PagerControls
          pager={pager}
          total={photos.data?.total}
          pageCount={photos.data?.pageCount}
          rowCount={rows.length}
          busy={photos.isFetching}
          label={heading}
        />
      </CardContent>
    </Card>
  );
}

const FRAUD_COLUMNS = 7;

/**
 * `fraud_review` navbati.
 *
 * Kompensatsiya xabari ATAYLAB ko'rsatiladi: server biletning oyini ko'chirgan bo'lishi mumkin
 * (uning o'z oyi allaqachon o'ynalgan bo'lsa), va bu qaror operatorga ko'rinmasa, u foydalanuvchi
 * bilan gaplashganda "bileting qaytdi" deyish bilan cheklanardi — qaysi efirga qaytganini
 * bilmasdan.
 */
function FraudQueue({ page, onPageChange }: TabPanelProps) {
  const pager = useOffsetPager({ page, onPageChange });
  const queue = useFraudQueue({ page: pager.page, limit: pager.limit });
  const moderate = useModerateTicket();
  const [reasons, setReasons] = useState<Record<string, string>>({});

  const submit = (ticket: AdminFraudTicket, action: 'restore' | 'revoke') => {
    const reason = (reasons[ticket.id] ?? '').trim();
    if (reason === '') return;
    moderate.mutate({ ticketId: ticket.id, action, reason });
  };

  const rows = queue.data?.items ?? [];
  const queueLength = queue.data?.total ?? 0;

  return (
    <Card className="border-warning/40">
      <CardHeader>
        <CardTitle>{strings.moderation.fraudHeading}</CardTitle>
        <CardDescription>{strings.moderation.fraudSubtitle}</CardDescription>
        <CardAction>
          {/*
            Navbat UZUNLIGI, sahifadagi qatorlar emas: bu raqam operator kunni boshlaganda
            ko'radigan birinchi narsa va u "yana bormi" degan savolni yopishi kerak.
          */}
          <Badge variant={queueLength > 0 ? 'warning' : 'secondary'} className="tabular">
            {queueLength}
          </Badge>
        </CardAction>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {queue.isError ? <ErrorNotice error={queue.error} /> : null}
        {moderate.isError ? <ErrorNotice error={moderate.error} /> : null}
        {moderate.isSuccess ? (
          <SuccessNotice>
            {moderate.data.ticket.status === 'revoked'
              ? strings.moderation.revokedDone
              : strings.moderation.restored}
            {moderate.data.compensated
              ? ` ${strings.moderation.compensated.replace('{month}', moderate.data.monthKey)}`
              : ''}
          </SuccessNotice>
        ) : null}

        {queue.isSuccess && rows.length === 0 ? (
          <EmptyState title={strings.moderation.fraudEmpty} icon={ShieldAlertIcon} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{strings.reveal.winnerTicket}</TableHead>
                <TableHead>{strings.moderation.columnUser}</TableHead>
                <TableHead>{strings.moderation.columnAcquired}</TableHead>
                <TableHead>{strings.moderation.columnMonth}</TableHead>
                <TableHead className="text-right">{strings.moderation.columnTotal}</TableHead>
                <TableHead>{strings.moderation.columnVerifiedBy}</TableHead>
                <TableHead className="md:sticky md:right-0 md:bg-card md:shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.12)]">
                  {strings.moderation.columnActions}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {queue.isPending ? <TableSkeleton columns={FRAUD_COLUMNS} /> : null}
              {rows.map((ticket) => {
                const reason = reasons[ticket.id] ?? '';
                const ready = reason.trim() !== '' && !moderate.isPending;

                return (
                  <TableRow key={ticket.id}>
                    <TableCell className="font-mono font-medium">
                      <Link
                        to="/moderation/tickets/$ticketId"
                        params={{ ticketId: ticket.id }}
                        className="text-primary hover:underline"
                      >
                        {ticket.code}
                      </Link>
                    </TableCell>
                    <TableCell className="tabular font-mono">{ticket.telegramId}</TableCell>
                    <TableCell className="tabular">{ticket.acquiredDate}</TableCell>
                    <TableCell className="tabular">{ticket.monthKey}</TableCell>
                    <TableCell className="tabular text-right">
                      {ticket.receiptTotal ?? strings.draws.noWinner}
                    </TableCell>
                    <TableCell>{ticket.receiptTotalVerifiedBy ?? strings.draws.noWinner}</TableCell>
                    <TableCell className="md:sticky md:right-0 md:bg-card md:shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.12)]">
                      <div className="flex items-center gap-2">
                        <Input
                          className="h-8 w-44"
                          placeholder={strings.moderation.reasonRequired}
                          aria-label={`${strings.moderation.reason} — ${ticket.code}`}
                          value={reason}
                          onChange={(event) =>
                            setReasons({ ...reasons, [ticket.id]: event.target.value })
                          }
                        />
                        <Button
                          size="sm"
                          disabled={!ready}
                          onClick={() => submit(ticket, 'restore')}
                        >
                          <CheckIcon aria-hidden="true" />
                          {strings.moderation.restore}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={!ready}
                          onClick={() => submit(ticket, 'revoke')}
                        >
                          <XIcon aria-hidden="true" />
                          {strings.moderation.revoke}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        <PagerControls
          pager={pager}
          total={queue.data?.total}
          pageCount={queue.data?.pageCount}
          rowCount={rows.length}
          busy={queue.isFetching}
          label={strings.moderation.fraudHeading}
        />
      </CardContent>
    </Card>
  );
}
